import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Bot, Send, AlertTriangle, AlertCircle, Info, RefreshCw,
  MessageSquare, Bell, BookOpen, BarChart3, ChevronDown,
  Plus, Trash2, Edit2, Check, X, Phone, Loader2,
  TrendingUp, TrendingDown, Calendar, DollarSign, Users,
  Shield, Zap, Clock, Eye, GraduationCap, UserCheck
} from "lucide-react";
import { TeamHubPanel, TrainingHubPanel, EscalationsPanel } from "./DoozyBotTeamPanels";
import EnhancedTrainingHub from "./EnhancedTrainingHub";
import DOMPurify from "dompurify";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ─── Severity badge ───
const SeverityBadge = ({ severity }) => {
  const cfg = {
    high: { bg: "bg-red-100 text-red-700 border-red-200", icon: AlertTriangle, label: "HIGH" },
    medium: { bg: "bg-amber-100 text-amber-700 border-amber-200", icon: AlertCircle, label: "MED" },
    low: { bg: "bg-blue-100 text-blue-700 border-blue-200", icon: Info, label: "LOW" },
  }[severity] || { bg: "bg-gray-100 text-gray-600 border-gray-200", icon: Info, label: severity };
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border ${cfg.bg}`} data-testid={`severity-${severity}`}>
      <Icon size={10} /> {cfg.label}
    </span>
  );
};

// ─── Department badge ───
const DeptBadge = ({ dept }) => {
  const colors = {
    ops: "bg-indigo-100 text-indigo-700",
    crm: "bg-emerald-100 text-emerald-700",
    finance: "bg-amber-100 text-amber-700",
    security: "bg-red-100 text-red-700",
  };
  return <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${colors[dept] || "bg-gray-100 text-gray-600"}`}>{dept?.toUpperCase()}</span>;
};

// ─── Stat Card ───
const StatCard = ({ icon: Icon, label, value, sub, color = "text-[#1E1B4B]" }) => (
  <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
    <div className="flex items-center gap-2 mb-1">
      <Icon size={14} className="text-[#E83E8C]" />
      <span className="text-[10px] font-semibold text-[#94a3b8] uppercase tracking-wider">{label}</span>
    </div>
    <p className={`text-2xl font-bold ${color}`}>{value}</p>
    {sub && <p className="text-[11px] text-[#94a3b8] mt-0.5">{sub}</p>}
  </div>
);

// ═══════════════════════════════════════════════════════════════
// CHAT PANEL — ChatGPT-style: multi-session sidebar + persistent history
// ═══════════════════════════════════════════════════════════════
const ACTIVE_SESSION_KEY = "doozybot_admin_active_session";

const ChatPanel = ({ authAxios }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState("");
  const [sessions, setSessions] = useState([]);
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const bottomRef = useRef(null);

  const refreshSessions = useCallback(async () => {
    try {
      const r = await authAxios.get(`${API_URL}/api/doozybot/chat/sessions`);
      return r.data.sessions || [];
    } catch { return []; }
  }, [authAxios]);

  const loadSession = useCallback(async (sid) => {
    if (!sid) return;
    try {
      const r = await authAxios.get(`${API_URL}/api/doozybot/chat/sessions/${sid}/messages`);
      setMessages(r.data.messages || []);
      setSessionId(sid);
      localStorage.setItem(ACTIVE_SESSION_KEY, sid);
    } catch {
      setMessages([]);
    }
  }, [authAxios]);

  const createNewSession = useCallback(async () => {
    try {
      const r = await authAxios.post(`${API_URL}/api/doozybot/chat/sessions`);
      const newSid = r.data.session_id;
      const updated = await refreshSessions();
      setSessions(updated);
      setMessages([]);
      setSessionId(newSid);
      localStorage.setItem(ACTIVE_SESSION_KEY, newSid);
    } catch { /* ignore */ }
  }, [authAxios, refreshSessions]);

  // Hydrate on mount — pick saved active session or first one, or create new
  useEffect(() => {
    (async () => {
      const list = await refreshSessions();
      setSessions(list);
      const saved = localStorage.getItem(ACTIVE_SESSION_KEY);
      const match = saved && list.find(s => s.session_id === saved);
      if (match) {
        await loadSession(saved);
      } else if (list.length > 0) {
        await loadSession(list[0].session_id);
      } else {
        await createNewSession();
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || sending) return;
    const isFirstMsg = messages.length === 0;
    setInput("");
    setMessages(prev => [...prev, { role: "user", content: text }]);
    setSending(true);
    try {
      const r = await authAxios.post(`${API_URL}/api/doozybot/chat`, {
        message: text, session_id: sessionId,
      });
      setMessages(prev => [...prev, { role: "assistant", content: r.data.response }]);
      // Backend auto-titles the session from the first user message — refresh list.
      if (isFirstMsg) {
        const list = await refreshSessions();
        setSessions(list);
      } else {
        // Bump this session to the top (updated_at changed)
        setSessions(prev => {
          const idx = prev.findIndex(s => s.session_id === sessionId);
          if (idx === -1) return prev;
          const copy = [...prev];
          const [item] = copy.splice(idx, 1);
          return [{ ...item, updated_at: new Date().toISOString() }, ...copy];
        });
      }
    } catch (err) {
      setMessages(prev => [...prev, { role: "assistant", content: "Sorry, I encountered an error. Please try again." }]);
    }
    setSending(false);
  };

  const renameSession = async (sid) => {
    const title = renameValue.trim();
    if (!title) { setRenamingId(null); return; }
    try {
      await authAxios.put(`${API_URL}/api/doozybot/chat/sessions/${sid}`, { title });
      setSessions(prev => prev.map(s => s.session_id === sid ? { ...s, title } : s));
    } catch { /* ignore */ }
    setRenamingId(null); setRenameValue("");
  };

  const deleteSession = async (sid) => {
    if (!window.confirm("Delete this chat permanently?")) return;
    try {
      await authAxios.delete(`${API_URL}/api/doozybot/chat/sessions/${sid}`);
      const list = await refreshSessions();
      setSessions(list);
      if (sid === sessionId) {
        if (list.length > 0) await loadSession(list[0].session_id);
        else await createNewSession();
      }
    } catch { /* ignore */ }
  };

  return (
    <div className="flex h-[560px] bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden" data-testid="doozybot-chat">
      {/* Sidebar — ChatGPT-style session list */}
      {sidebarOpen && (
        <aside className="w-[220px] flex-shrink-0 border-r border-gray-100 bg-[#FAFAFB] flex flex-col" data-testid="doozybot-chat-sidebar">
          <div className="p-3 border-b border-gray-100">
            <button
              onClick={createNewSession}
              className="w-full flex items-center justify-center gap-1.5 py-2 rounded-xl bg-[#E83E8C] text-white text-xs font-bold hover:bg-[#d1356f] transition-colors"
              data-testid="doozybot-new-chat-btn"
            >
              <Plus size={12} /> New chat
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-2 space-y-1">
            {sessions.length === 0 && (
              <p className="text-[11px] text-gray-400 text-center mt-6">No chats yet</p>
            )}
            {sessions.map(s => {
              const active = s.session_id === sessionId;
              const renaming = renamingId === s.session_id;
              return (
                <div
                  key={s.session_id}
                  className={`group flex items-center gap-1 px-2 py-1.5 rounded-lg transition-all ${
                    active ? "bg-[#E83E8C]/10 border border-[#E83E8C]/30" : "hover:bg-white border border-transparent"
                  }`}
                  data-testid={`doozybot-session-${s.session_id}`}
                >
                  {renaming ? (
                    <input
                      autoFocus
                      value={renameValue}
                      onChange={(e) => setRenameValue(e.target.value)}
                      onBlur={() => renameSession(s.session_id)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") renameSession(s.session_id);
                        if (e.key === "Escape") { setRenamingId(null); setRenameValue(""); }
                      }}
                      className="flex-1 min-w-0 px-1.5 py-0.5 text-[11px] rounded border border-[#E83E8C]/50 outline-none bg-white"
                      data-testid="doozybot-rename-input"
                    />
                  ) : (
                    <button
                      onClick={() => loadSession(s.session_id)}
                      className={`flex-1 min-w-0 text-left text-[11px] font-medium truncate ${active ? "text-[#E83E8C]" : "text-[#1E1B4B]"}`}
                      title={s.title}
                    >
                      {s.title}
                    </button>
                  )}
                  {!renaming && (
                    <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => { setRenamingId(s.session_id); setRenameValue(s.title); }}
                        className="p-1 rounded hover:bg-white"
                        title="Rename"
                        data-testid={`doozybot-rename-${s.session_id}`}
                      >
                        <Edit2 size={10} className="text-gray-400" />
                      </button>
                      <button
                        onClick={() => deleteSession(s.session_id)}
                        className="p-1 rounded hover:bg-red-50"
                        title="Delete"
                        data-testid={`doozybot-delete-${s.session_id}`}
                      >
                        <Trash2 size={10} className="text-red-400" />
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </aside>
      )}

      <div className="flex-1 flex flex-col min-w-0">
      {/* Header */}
      <div className="px-4 py-3 bg-gradient-to-r from-[#1E1B4B] to-indigo-800 flex items-center gap-3">
        <button
          onClick={() => setSidebarOpen(p => !p)}
          className="text-white/70 hover:text-white p-1.5 rounded-lg hover:bg-white/10"
          title={sidebarOpen ? "Hide chat list" : "Show chat list"}
          data-testid="doozybot-sidebar-toggle"
        >
          <MessageSquare size={16} />
        </button>
        <div className="w-9 h-9 rounded-full bg-[#E83E8C]/20 flex items-center justify-center">
          <Bot size={18} className="text-[#E83E8C]" />
        </div>
        <div className="min-w-0">
          <p className="text-white text-sm font-bold truncate">DoozyBot</p>
          <p className="text-white/50 text-[10px]">AI Operations Manager</p>
        </div>
        <span className="ml-auto flex items-center gap-1 text-[10px] text-emerald-300">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> Online
        </span>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3 bg-[#FAFAFA]">
        {messages.length === 0 && (
          <div className="text-center py-10">
            <Bot size={40} className="text-[#E83E8C]/30 mx-auto mb-3" />
            <p className="text-sm font-medium text-[#1E1B4B]">Hey Boss! I'm DoozyBot</p>
            <p className="text-xs text-[#94a3b8] mt-1 max-w-xs mx-auto">
              Ask me about bookings, revenue, team performance, or anything about DoozyGo operations.
            </p>
            <div className="flex flex-wrap gap-2 justify-center mt-4">
              {["How's business today?", "Any urgent issues?", "Show me trends this month", "Suggest future improvements", "Revenue forecast", "Who needs follow-up?"].map(q => (
                <button key={q} onClick={() => { setInput(q); }} className="px-3 py-1.5 rounded-full text-[11px] bg-white border border-gray-200 text-[#475569] hover:border-[#E83E8C]/40 hover:bg-[#E83E8C]/5 transition-all" data-testid="chat-suggestion">
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((msg, i) => (
          <div key={`${msg.role}-${i}-${msg.content?.slice(0,10)}`} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
              msg.role === "user"
                ? "bg-[#E83E8C] text-white rounded-br-md"
                : "bg-white border border-gray-100 text-[#1E1B4B] rounded-bl-md shadow-sm"
            }`}>
              {msg.role === "assistant" ? (
                <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(msg.content
                  .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                  .replace(/\*(.*?)\*/g, '<em>$1</em>')
                  .replace(/^- (.+)/gm, '<li style="margin-left:16px;list-style:disc">$1</li>')
                  .replace(/^\* (.+)/gm, '<li style="margin-left:16px;list-style:disc">$1</li>')
                  .replace(/^(\d+)\. (.+)/gm, '<li style="margin-left:16px;list-style:decimal">$2</li>')
                  .replace(/\[([A-Z]+)\]/g, '<span style="background:#fee2e2;color:#991b1b;padding:1px 6px;border-radius:4px;font-size:10px;font-weight:700">$1</span>')
                  .replace(/---/g, '<hr style="border-color:#e5e7eb;margin:8px 0">')
                  .replace(/\n/g, '<br/>'))
                }} />
              ) : (
                <span style={{ whiteSpace: "pre-wrap" }}>{msg.content}</span>
              )}
            </div>
          </div>
        ))}
        {sending && (
          <div className="flex justify-start">
            <div className="bg-white border border-gray-100 rounded-2xl rounded-bl-md px-4 py-3 shadow-sm">
              <div className="flex gap-1.5">
                <span className="w-2 h-2 rounded-full bg-[#E83E8C] animate-bounce" style={{ animationDelay: "0s" }} />
                <span className="w-2 h-2 rounded-full bg-[#E83E8C] animate-bounce" style={{ animationDelay: "0.15s" }} />
                <span className="w-2 h-2 rounded-full bg-[#E83E8C] animate-bounce" style={{ animationDelay: "0.3s" }} />
              </div>
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <div className="px-4 py-3 border-t border-gray-100 bg-white">
        <div className="flex gap-2">
          <input
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === "Enter" && !e.shiftKey && sendMessage()}
            placeholder="Ask DoozyBot anything..."
            className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:border-[#E83E8C] outline-none bg-[#FAFAFA]"
            disabled={sending || !sessionId}
            data-testid="doozybot-chat-input"
          />
          <button onClick={sendMessage} disabled={!input.trim() || sending || !sessionId}
            className="px-4 py-2.5 rounded-xl bg-[#E83E8C] text-white disabled:opacity-40 hover:bg-[#d1356f] transition-colors"
            data-testid="doozybot-send-btn">
            <Send size={16} />
          </button>
        </div>
      </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// ALERTS PANEL
// ═══════════════════════════════════════════════════════════════
const AlertsPanel = ({ authAxios }) => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState({ total: 0, high: 0, medium: 0, low: 0 });

  const fetchAlerts = useCallback(async () => {
    setLoading(true);
    try {
      const r = await authAxios.get(`${API_URL}/api/doozybot/alerts`);
      setAlerts(r.data.alerts || []);
      setSummary({ total: r.data.total, high: r.data.high, medium: r.data.medium, low: r.data.low });
    } catch { }
    setLoading(false);
  }, [authAxios]);

  useEffect(() => { fetchAlerts(); }, [fetchAlerts]);

  return (
    <div data-testid="doozybot-alerts">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <h3 className="text-base font-bold text-[#1E1B4B]">Active Alerts</h3>
          {summary.high > 0 && <span className="px-2.5 py-1 rounded-full bg-red-100 text-red-700 text-xs font-bold animate-pulse">{summary.high} Critical</span>}
          {summary.medium > 0 && <span className="px-2.5 py-1 rounded-full bg-amber-100 text-amber-700 text-xs font-bold">{summary.medium} Warning</span>}
        </div>
        <button onClick={fetchAlerts} className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium text-[#475569] hover:bg-gray-100 transition-colors" data-testid="refresh-alerts-btn">
          <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Scan Now
        </button>
      </div>

      {loading ? (
        <div className="text-center py-10"><Loader2 className="animate-spin text-[#E83E8C] mx-auto" size={24} /></div>
      ) : alerts.length === 0 ? (
        <div className="text-center py-10 bg-emerald-50 rounded-xl border border-emerald-200">
          <Check size={32} className="text-emerald-500 mx-auto mb-2" />
          <p className="text-sm font-semibold text-emerald-700">All Clear!</p>
          <p className="text-xs text-emerald-600/70">No active alerts. Everything's running smoothly.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {alerts.map((alert, i) => (
            <div key={`${alert.type}-${alert.entity_id || i}`} className={`p-3 rounded-xl border bg-white transition-all hover:shadow-sm ${
              alert.severity === "high" ? "border-red-200" : alert.severity === "medium" ? "border-amber-200" : "border-gray-100"
            }`} data-testid={`alert-item-${i}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <SeverityBadge severity={alert.severity} />
                    <DeptBadge dept={alert.department} />
                  </div>
                  <p className="text-sm font-medium text-[#1E1B4B]">{alert.title}</p>
                  <p className="text-xs text-[#94a3b8] mt-0.5">{alert.detail}</p>
                </div>
                {alert.phone && (
                  <button className="flex-shrink-0 p-2 rounded-lg bg-[#25D366]/10 text-[#25D366] hover:bg-[#25D366]/20 transition-colors" title="Nudge via WhatsApp" data-testid={`nudge-btn-${i}`}>
                    <Phone size={14} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// KNOWLEDGE BASE PANEL
// ═══════════════════════════════════════════════════════════════
const KnowledgePanel = ({ authAxios }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ category: "faq", question: "", answer: "", topic: "", content: "" });

  const fetchKB = useCallback(async () => {
    setLoading(true);
    try {
      const r = await authAxios.get(`${API_URL}/api/doozybot/knowledge`);
      setItems(r.data.items || []);
    } catch { }
    setLoading(false);
  }, [authAxios]);

  useEffect(() => { fetchKB(); }, [fetchKB]);

  const handleSave = async () => {
    try {
      if (editId) {
        await authAxios.put(`${API_URL}/api/doozybot/knowledge/${editId}`, form);
      } else {
        await authAxios.post(`${API_URL}/api/doozybot/knowledge`, form);
      }
      setShowAdd(false); setEditId(null);
      setForm({ category: "faq", question: "", answer: "", topic: "", content: "" });
      fetchKB();
    } catch { }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this knowledge entry?")) return;
    try {
      await authAxios.delete(`${API_URL}/api/doozybot/knowledge/${id}`);
      fetchKB();
    } catch { }
  };

  const startEdit = (item) => {
    setEditId(item.id);
    setForm({ category: item.category, question: item.question || "", answer: item.answer || "", topic: item.topic || "", content: item.content || "" });
    setShowAdd(true);
  };

  return (
    <div data-testid="doozybot-knowledge">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base font-bold text-[#1E1B4B]">Knowledge Base ({items.length})</h3>
        <button onClick={() => { setShowAdd(true); setEditId(null); setForm({ category: "faq", question: "", answer: "", topic: "", content: "" }); }}
          className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-[#E83E8C] text-white text-xs font-medium hover:bg-[#d1356f] transition-colors" data-testid="add-knowledge-btn">
          <Plus size={12} /> Add Entry
        </button>
      </div>

      {/* Add/Edit Form */}
      {showAdd && (
        <div className="bg-white rounded-xl border border-[#E83E8C]/20 p-4 mb-4 space-y-3" data-testid="knowledge-form">
          <div className="flex items-center gap-2">
            <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
              className="px-3 py-2 rounded-lg border text-xs" data-testid="kb-category-select">
              <option value="faq">FAQ</option>
              <option value="info">Info</option>
              <option value="process">Process</option>
              <option value="policy">Policy</option>
            </select>
            <span className="text-xs text-[#94a3b8]">{editId ? "Editing" : "New entry"}</span>
          </div>
          {form.category === "faq" ? (
            <>
              <input value={form.question} onChange={e => setForm(f => ({ ...f, question: e.target.value }))}
                placeholder="Question" className="w-full px-3 py-2 rounded-lg border text-sm" data-testid="kb-question-input" />
              <textarea value={form.answer} onChange={e => setForm(f => ({ ...f, answer: e.target.value }))}
                placeholder="Answer" rows={3} className="w-full px-3 py-2 rounded-lg border text-sm" data-testid="kb-answer-input" />
            </>
          ) : (
            <>
              <input value={form.topic} onChange={e => setForm(f => ({ ...f, topic: e.target.value }))}
                placeholder="Topic title" className="w-full px-3 py-2 rounded-lg border text-sm" data-testid="kb-topic-input" />
              <textarea value={form.content} onChange={e => setForm(f => ({ ...f, content: e.target.value }))}
                placeholder="Content" rows={4} className="w-full px-3 py-2 rounded-lg border text-sm" data-testid="kb-content-input" />
            </>
          )}
          <div className="flex gap-2">
            <button onClick={handleSave} className="px-4 py-2 rounded-lg bg-[#E83E8C] text-white text-xs font-medium" data-testid="kb-save-btn">
              {editId ? "Update" : "Add"}
            </button>
            <button onClick={() => { setShowAdd(false); setEditId(null); }} className="px-4 py-2 rounded-lg border text-xs" data-testid="kb-cancel-btn">Cancel</button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-center py-10"><Loader2 className="animate-spin text-[#E83E8C] mx-auto" size={24} /></div>
      ) : (
        <div className="space-y-2 max-h-[500px] overflow-y-auto pr-1">
          {items.map(item => (
            <div key={item.id} className="bg-white rounded-xl border border-gray-100 p-3 hover:shadow-sm transition-all" data-testid={`kb-item-${item.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-purple-100 text-purple-700">{item.category}</span>
                    {!item.active && <span className="px-2 py-0.5 rounded text-[10px] bg-gray-100 text-gray-500">Inactive</span>}
                  </div>
                  {item.question ? (
                    <>
                      <p className="text-sm font-medium text-[#1E1B4B] truncate">Q: {item.question}</p>
                      <p className="text-xs text-[#94a3b8] mt-0.5 line-clamp-2">A: {item.answer}</p>
                    </>
                  ) : (
                    <>
                      <p className="text-sm font-medium text-[#1E1B4B] truncate">{item.topic}</p>
                      <p className="text-xs text-[#94a3b8] mt-0.5 line-clamp-2">{item.content}</p>
                    </>
                  )}
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  <button onClick={() => startEdit(item)} className="p-1.5 rounded-lg hover:bg-gray-100" data-testid={`kb-edit-${item.id}`}><Edit2 size={12} className="text-[#475569]" /></button>
                  <button onClick={() => handleDelete(item.id)} className="p-1.5 rounded-lg hover:bg-red-50" data-testid={`kb-delete-${item.id}`}><Trash2 size={12} className="text-red-400" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// REPORTS PANEL
// ═══════════════════════════════════════════════════════════════
const ReportsPanel = ({ authAxios }) => {
  const [daily, setDaily] = useState(null);
  const [weekly, setWeekly] = useState(null);
  const [tab, setTab] = useState("daily");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try {
        const [d, w] = await Promise.all([
          authAxios.get(`${API_URL}/api/doozybot/report/daily`),
          authAxios.get(`${API_URL}/api/doozybot/report/weekly`),
        ]);
        setDaily(d.data);
        setWeekly(w.data);
      } catch { }
      setLoading(false);
    };
    fetch();
  }, [authAxios]);

  if (loading) return <div className="text-center py-10"><Loader2 className="animate-spin text-[#E83E8C] mx-auto" size={24} /></div>;

  return (
    <div data-testid="doozybot-reports">
      <div className="flex gap-2 mb-4">
        {["daily", "weekly"].map(t => (
          <button key={t} onClick={() => setTab(t)}
            className={`px-4 py-2 rounded-lg text-xs font-medium transition-all ${tab === t ? "bg-[#1E1B4B] text-white" : "bg-white text-[#475569] border border-gray-200"}`}
            data-testid={`report-tab-${t}`}>
            {t === "daily" ? "Today's Digest" : "Weekly Analytics"}
          </button>
        ))}
      </div>

      {tab === "daily" && daily && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <StatCard icon={Calendar} label="Today's Bookings" value={daily.bookings.today_count} sub={`${daily.bookings.tomorrow_count} tomorrow`} />
            <StatCard icon={DollarSign} label="Revenue Today" value={`Rs ${(daily.revenue.today_total || 0).toLocaleString()}`} sub={`Rs ${(daily.revenue.today_collected || 0).toLocaleString()} collected`} />
            <StatCard icon={Users} label="New Leads" value={daily.crm.leads_today} sub={`${daily.crm.whatsapp_messages} WA messages`} />
            <StatCard icon={AlertTriangle} label="Active Alerts" value={daily.alerts.total}
              sub={daily.alerts.high > 0 ? `${daily.alerts.high} critical` : "All clear"}
              color={daily.alerts.high > 0 ? "text-red-600" : "text-emerald-600"} />
          </div>

          {daily.bookings.today.length > 0 && (
            <div>
              <h4 className="text-sm font-bold text-[#1E1B4B] mb-2">Today's Schedule</h4>
              <div className="space-y-1.5">
                {daily.bookings.today.map(b => (
                  <div key={b.id} className="flex items-center justify-between p-3 bg-white rounded-xl border border-gray-100">
                    <div>
                      <p className="text-sm font-medium text-[#1E1B4B]">{b.customer_name}</p>
                      <p className="text-xs text-[#94a3b8]">{b.package} — {b.occasion} {b.setup_time ? `at ${b.setup_time}` : ""}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold text-[#E83E8C]">Rs {(b.total_amount || 0).toLocaleString()}</p>
                      <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${
                        b.booking_status === "confirmed" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"
                      }`}>{b.booking_status}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {daily.alerts.items?.length > 0 && (
            <div>
              <h4 className="text-sm font-bold text-[#1E1B4B] mb-2">Top Alerts</h4>
              <div className="space-y-1.5">
                {daily.alerts.items.slice(0, 5).map((a, i) => (
                  <div key={`${a.type}-${a.entity_id || i}`} className="flex items-center gap-2 p-2 rounded-lg bg-white border border-gray-100">
                    <SeverityBadge severity={a.severity} />
                    <span className="text-xs text-[#1E1B4B] flex-1">{a.title}</span>
                    <DeptBadge dept={a.department} />
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-4 border border-indigo-100">
            <div className="flex items-center gap-2 mb-1">
              <TrendingUp size={14} className="text-indigo-600" />
              <span className="text-xs font-semibold text-indigo-700">Week over Week</span>
            </div>
            <p className="text-lg font-bold text-[#1E1B4B]">
              {daily.weekly.this_week} bookings this week
              <span className={`ml-2 text-sm ${daily.weekly.growth >= 0 ? "text-emerald-600" : "text-red-500"}`}>
                {daily.weekly.growth >= 0 ? "+" : ""}{daily.weekly.growth}%
              </span>
            </p>
            <p className="text-xs text-[#94a3b8]">vs {daily.weekly.prev_week} last week</p>
          </div>
        </div>
      )}

      {tab === "weekly" && weekly && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <StatCard icon={DollarSign} label="Week Revenue" value={`Rs ${(weekly.revenue.this_week || 0).toLocaleString()}`}
              sub={<span className={weekly.revenue.growth_pct >= 0 ? "text-emerald-600" : "text-red-500"}>
                {weekly.revenue.growth_pct >= 0 ? "+" : ""}{weekly.revenue.growth_pct}% vs last week
              </span>} />
            <StatCard icon={Calendar} label="Bookings" value={weekly.bookings.this_week} sub={`${weekly.bookings.prev_week} last week`} />
            <StatCard icon={Users} label="New Customers" value={weekly.new_customers} />
            <StatCard icon={X} label="Cancellation Rate" value={`${weekly.bookings.cancellation_rate}%`}
              color={weekly.bookings.cancellation_rate > 15 ? "text-red-600" : "text-emerald-600"} />
          </div>

          {weekly.packages?.length > 0 && (
            <div>
              <h4 className="text-sm font-bold text-[#1E1B4B] mb-2">Package Breakdown</h4>
              <div className="grid grid-cols-2 gap-2">
                {weekly.packages.map(p => (
                  <div key={p.package} className="bg-white rounded-xl border border-gray-100 p-3">
                    <p className="text-sm font-bold text-[#1E1B4B]">{p.package || "N/A"}</p>
                    <p className="text-xs text-[#94a3b8]">{p.count} bookings — Rs {(p.revenue || 0).toLocaleString()}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {weekly.daily_breakdown?.length > 0 && (
            <div>
              <h4 className="text-sm font-bold text-[#1E1B4B] mb-2">Daily Breakdown</h4>
              <div className="space-y-1.5">
                {weekly.daily_breakdown.map(d => (
                  <div key={d.date} className="flex items-center justify-between p-2 rounded-lg bg-white border border-gray-100">
                    <span className="text-xs font-medium text-[#1E1B4B]">{d.date}</span>
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-[#94a3b8]">{d.bookings} bookings</span>
                      <span className="text-xs font-bold text-[#E83E8C]">Rs {(d.revenue || 0).toLocaleString()}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// MAIN TAB COMPONENT
// ═══════════════════════════════════════════════════════════════
const DoozyBotTab = ({ auth, authAxios }) => {
  const [subTab, setSubTab] = useState("chat");

  const SUB_TABS = [
    { id: "chat", label: "Chat", icon: MessageSquare },
    { id: "alerts", label: "Alerts", icon: Bell },
    { id: "team", label: "Team Hub", icon: UserCheck },
    { id: "training", label: "Training", icon: GraduationCap },
    { id: "reports", label: "Reports", icon: BarChart3 },
    { id: "knowledge", label: "Knowledge", icon: BookOpen },
  ];

  return (
    <div data-testid="doozybot-tab">
      {/* Sub-tab nav */}
      <div className="flex gap-2 mb-5">
        {SUB_TABS.map(t => {
          const Icon = t.icon;
          return (
            <button key={t.id} onClick={() => setSubTab(t.id)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-medium transition-all ${
                subTab === t.id ? "bg-[#1E1B4B] text-white shadow-md" : "bg-white text-[#475569] border border-gray-200 hover:border-[#E83E8C]/30"
              }`} data-testid={`doozybot-subtab-${t.id}`}>
              <Icon size={14} /> {t.label}
            </button>
          );
        })}
      </div>

      {/* Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {subTab === "chat" && (
          <>
            <div className="lg:col-span-2">
              <ChatPanel authAxios={authAxios} />
            </div>
            <div>
              <AlertsPanel authAxios={authAxios} />
            </div>
          </>
        )}
        {subTab === "alerts" && (
          <>
            <div className="lg:col-span-2">
              <AlertsPanel authAxios={authAxios} />
            </div>
            <div>
              <EscalationsPanel authAxios={authAxios} />
            </div>
          </>
        )}
        {subTab === "team" && (
          <div className="lg:col-span-3">
            <TeamHubPanel authAxios={authAxios} />
          </div>
        )}
        {subTab === "training" && (
          <div className="lg:col-span-3">
            <EnhancedTrainingHub authAxios={authAxios} />
          </div>
        )}
        {subTab === "reports" && (
          <div className="lg:col-span-3">
            <ReportsPanel authAxios={authAxios} />
          </div>
        )}
        {subTab === "knowledge" && (
          <div className="lg:col-span-3">
            <KnowledgePanel authAxios={authAxios} />
          </div>
        )}
      </div>
    </div>
  );
};

export default DoozyBotTab;
